/** Published guide content is owned by Firestore, not the frontend release. */
export const PUBLISHED_GUIDES_COLLECTION = 'theaWebPublishedGuides';
export const PUBLISHED_GUIDE_INDEX_COLLECTION = 'theaWebPublishedGuideIndex';
export const PUBLISHED_GUIDE_INDEX_ID = 'current';
export const GUIDE_BROWSE_TILE_COUNT = 3;

export interface PublishedGuide {
  slug: string;
  title: string;
  emoji: string;
  productIds: string[];
  accent?: { soft: string; glow: string };
  /**
   * The shopper-facing intro: 1–2 warm sentences under the <h1>, and the same
   * string the prerender uses for <meta name="description"> (THE-675).
   *
   * Named `blurb`, NOT `description`, on purpose. Every published guide doc
   * already carries `matching.description` — the recipient sentence the guide
   * MATCHER embeds, in the house form "<recipient> — <item kinds>." (66/66 in
   * production). That string addresses the ranker, not the shopper, and a
   * top-level `description` sitting beside it is an invitation to seed one
   * into the other. Two audiences, two names.
   *
   * Optional, and absent on every guide until the copy is seeded — the header
   * renders without it (see GuideBoardView), so a guide is never broken by
   * having no blurb yet.
   */
  blurb?: string;
}

export interface PublishedGuideListing {
  key: string;
  href: string;
  title: string;
  emoji: string;
  kind: 'guide';
  displayOrder: number;
  /** Public description and search terms from the prepared hub index. */
  searchText?: string;
  season?: { start: string; end: string };
  previewProductIds?: string[];
  previewImages?: string[];
  /**
   * `YYYY-MM-DD`, the day the guide became visible to shoppers (THE-817).
   *
   * OPTIONAL BY CONTRACT, not just at the start. The hub's "New guides" row is
   * the only reader, it selects on a window, and a listing with no date simply
   * does not qualify — so an un-dated guide costs one absent card in one row
   * rather than a parse failure that blanks the whole index. That matters more
   * than it sounds: this is ONE document for all 78 guides, so anything that
   * throws here takes down the hub grid, /gift-guides and every board rail at
   * once. See the writers' note in `optionalPublishedAt`.
   *
   * A DAY, NOT A TIMESTAMP, and that is deliberate. The window is counted in
   * days, the two sources the backfill derived from (a Firestore create time
   * and a git commit date) do not agree to better than a day anyway, and a
   * date-only string compares correctly with `<=`/`>=` as a plain string — the
   * same property `season` relies on.
   */
  publishedAt?: string;
}

const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid published guide document');
  }
  return value as Record<string, unknown>;
};
const string = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) throw new Error('Invalid guide text');
  return value;
};
const strings = (value: unknown): string[] => {
  if (!Array.isArray(value)) throw new Error('Invalid guide product list');
  return value.map(string);
};
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The blurb is the ONE field here that must not take a guide down.
 *
 * Everything else on the doc is load-bearing, so the strict `string()` above
 * is right for it: a guide with no title has nothing to render. The blurb is
 * decorative, it lands on 66 docs by a seeding pass, and it will be absent on
 * every one of them until that pass runs. Absent or blank therefore resolves
 * to `undefined` — the header simply omits the line — instead of throwing,
 * which `usePublishedDocument` would turn into the full-page "We couldn't
 * load this gift guide" for a missing sentence.
 *
 * A non-string still throws. That is not a guide waiting for copy, it is a
 * seeding bug, and it should be loud the first time anyone opens the page.
 */
const optionalBlurb = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') throw new Error('Invalid guide blurb');
  const trimmed = value.trim();
  return trimmed || undefined;
};

export function parsePublishedGuide(value: unknown, slug: string): PublishedGuide {
  const data = record(value);
  if (!slugPattern.test(slug) || data.slug !== slug) throw new Error('Invalid guide slug');
  const productIds = strings(data.productIds);
  if (productIds.some((id) => id.includes('/')) || new Set(productIds).size !== productIds.length) {
    throw new Error('Invalid guide product IDs');
  }
  const accent = data.accent === undefined ? undefined : record(data.accent);
  const blurb = optionalBlurb(data.blurb);
  return {
    slug, title: string(data.title), emoji: string(data.emoji), productIds,
    ...(accent ? { accent: { soft: string(accent.soft), glow: string(accent.glow) } } : {}),
    ...(blurb ? { blurb } : {}),
  };
}

function monthDay(value: unknown): string {
  const result = string(value);
  if (!/^\d{2}-\d{2}$/.test(result)) throw new Error('Invalid guide season');
  const [month, day] = result.split('-').map(Number);
  const date = new Date(Date.UTC(2000, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new Error('Invalid guide season');
  }
  return result;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `publishedAt` -> a `YYYY-MM-DD` string, or `undefined` for anything else.
 *
 * THIS FIELD NEVER THROWS, which breaks from every other field in the index
 * parser, and the reason is blast radius rather than taste. `displayOrder`,
 * `season` and `title` all throw because a listing missing them has nothing to
 * render. `publishedAt` is a FILTER INPUT for one row — a guide with no usable
 * date just isn't "new" — while a throw in `parsePublishedGuideIndex` fails the
 * ONE document that backs all 78 guides, so a single bad date string would
 * blank the hub grid, /gift-guides and every board guide rail together.
 *
 * Degrading beats failing here for the same reason `parseHubTrending`
 * truncates an over-long row instead of rejecting it: losing one card from one
 * row is strictly better than blanking a surface that is otherwise fine.
 *
 * Writers are where a bad date gets caught loudly instead: `scripts/guides.py`
 * and `scripts/hub_docs.py` in thea-serverless validate against this same
 * pattern before they write, so the console warning below should only ever be
 * reachable by a hand edit in the Firebase console.
 *
 * A real calendar check, not just the shape — `2026-02-31` matches the regex.
 */
const optionalPublishedAt = (value: unknown, slug: string): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day) {
      return value;
    }
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[published guides] ignoring unusable publishedAt on "${slug}": ${JSON.stringify(value)}`,
  );
  return undefined;
};

export function parsePublishedGuideIndex(value: unknown): PublishedGuideListing[] {
  return Object.entries(record(record(value).guides)).map(([slug, raw]) => {
    const data = record(raw);
    if (!slugPattern.test(slug) || data.key !== slug || data.href !== `/guide/${slug}` || data.kind !== 'guide') {
      throw new Error('Invalid published guide listing');
    }
    if (typeof data.displayOrder !== 'number' || !Number.isFinite(data.displayOrder)) {
      throw new Error('Invalid guide display order');
    }
    const season = data.season === undefined ? undefined : record(data.season);
    const publishedAt = optionalPublishedAt(data.publishedAt, slug);
    return {
      key: slug,
      href: `/guide/${slug}`,
      title: string(data.title),
      emoji: string(data.emoji),
      kind: 'guide' as const,
      displayOrder: data.displayOrder,
      ...(typeof data.searchText === 'string' ? { searchText: data.searchText.slice(0, 4000) } : {}),
      ...(season ? { season: { start: monthDay(season.start), end: monthDay(season.end) } } : {}),
      ...(publishedAt ? { publishedAt } : {}),
      ...(data.previewProductIds === undefined ? {} : { previewProductIds: strings(data.previewProductIds) }),
      ...(data.previewImages === undefined ? {} : { previewImages: strings(data.previewImages) }),
    };
  });
}

/**
 * The peek-strip image urls for one listing — THE single precedence rule, so
 * no surface can answer this question differently (THE-492).
 *
 * Two sources exist and neither covers every guide:
 *
 *  1. `listing.previewImages`, inline on the published index doc. Written by
 *     whoever publishes the guide, so a Firestore-only guide — one that was
 *     never added to thea-web's compiled registry — has art the moment it is
 *     published, with no deploy.
 *  2. `theaWebGuidePreviews/current`, the materialized map. Its writer
 *     extracts the guide→product map from a thea-web master sha
 *     (`builtFromSha`), so it can only ever hold keys the REGISTRY knows.
 *
 * Inline wins, and the materialized map is the fallback: that ordering is the
 * only one under which both a Firestore-only guide and a registry guide get a
 * full strip. It was already what /gift-guides did; the rails read source 2
 * alone, which is exactly why "Housewarming" — published in Firestore, absent
 * from the registry, and so absent from the materialized doc — rendered three
 * placeholder tiles on a board while looking fine on /gift-guides. Six guides
 * were in that state (anniversary, big-play-small-spaces, cult-favorite-beauty,
 * graduation, housewarming, movie-lover).
 *
 * Takes a structural listing rather than PublishedGuideListing so the rails,
 * which are still typed against the registry's GuideBrowseListing, can call it
 * without either module learning about the other's type.
 */
export function guidePeekImageUrls(
  listing: { key: string; previewImages?: string[] },
  previews: Record<string, string[]> | undefined,
): string[] {
  return listing.previewImages ?? previews?.[listing.key] ?? [];
}

export function visiblePublishedGuides(listings: PublishedGuideListing[], date: Date): PublishedGuideListing[] {
  const day = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return listings.filter(({ season }) => !season || (season.start <= season.end
    ? day >= season.start && day <= season.end
    : day >= season.start || day <= season.end))
    .sort((a, b) => a.displayOrder - b.displayOrder || a.key.localeCompare(b.key));
}
